<template>
  <default>
    <div v-if="order" class=" mx-auto p-6 space-y-8 dark:bg-gray-900 min-h-screen">

      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Objednávka č. {{ order.counterNumber }} <span class="text-sm">#{{order.id}}</span></h1>
        <button
            @click="confirmDelete"
            :disabled="deleteLoading"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg focus:ring-2 focus:ring-red-300"
        >
          <span v-if="deleteLoading">Mazání…</span>
          <span v-else>Smazat objednávku</span>
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 class="font-semibold text-lg mb-2 text-gray-800 dark:text-gray-100">Zákazník</h2>
          <p class="text-gray-700 dark:text-gray-300"><strong>Jméno:</strong> {{ order.customer.name }}</p>
          <p v-if="order.customer.email" class="text-gray-700 dark:text-gray-300"><strong>Email:</strong> {{ order.customer.email }}</p>
          <p v-if="order.customer.phone" class="text-gray-700 dark:text-gray-300"><strong>Telefon:</strong> {{ order.customer.phone }}</p>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 class="font-semibold text-lg mb-2 text-gray-800 dark:text-gray-100">Objednávka</h2>
          <div class="flex items-center gap-3 mb-2">
            <strong class="text-gray-700 dark:text-gray-300">Stav:</strong>
            <select
                v-model="selectedStatus"
                class="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option v-for="s in availableStatuses" :key="s" :value="s">{{ s }}</option>
            </select>
            <button
                v-if="selectedStatus !== order.status"
                @click="updateStatus"
                :disabled="statusLoading"
                class="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
            >
              <span v-if="statusLoading">Ukládám…</span>
              <span v-else>Uložit</span>
            </button>
          </div>
          <p class="text-gray-700 dark:text-gray-300"><strong>Vytvořeno:</strong> {{ formatDate(order.createdAt) }}</p>
          <p class="text-gray-700 dark:text-gray-300"><strong>Objednáno:</strong> {{ formatDate(order.orderedAt) }}</p>
        </div>
      </div>

      <!-- Adresy -->
      <div v-if="order.addresses" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div v-if="order.addresses.billing" class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Fakturační adresa</h2>
          <AddressBlock :address="order.addresses.billing" />
        </div>
        <div v-if="order.addresses.shipping" class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Doručovací adresa</h2>
          <AddressBlock :address="order.addresses.shipping" />
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-semibold text-lg text-gray-800 dark:text-gray-100">Položky</h2>
          <div class="flex items-center">
            <input type="checkbox" id="toggleCustom"
                   v-model="productCustomDataVisible"
                   class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500">
            <label for="toggleCustom" class="ml-2 text-sm text-gray-900 dark:text-gray-300">
              Zobrazit vlastní data položek
            </label>
          </div>
        </div>

        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead>
          <tr class="text-left text-gray-600 dark:text-gray-300">
            <th class="py-2">Název</th>
            <th class="py-2">Typ</th>
            <th class="py-2">Kód</th>
            <th class="py-2">jednotka</th>
            <th class="py-2">Množství</th>
            <th class="py-2">Custom</th>
          </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
          <tr v-for="item in order.items" :key="item._id" class="text-gray-800 dark:text-gray-200">
            <td class="py-2">{{ item.name }}</td>
            <td class="py-2">{{ item.custom.reclamation ? 'reklamace' : 'objednání' }}</td>
            <td class="py-2">{{ item.code }}</td>
            <td class="py-2">{{ item.custom?.orderUnit ?? item.unit }}</td>
            <td class="py-2">{{ item.quantity }}</td>
            <td class="py-2">
              <RecursiveJson :data="item.custom" v-if="productCustomDataVisible" />
              <div v-else>#</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>


      <div v-if="order.custom" class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h2 class="font-semibold text-lg mb-4 text-gray-800 dark:text-gray-100">Custom Data</h2>
        <RecursiveJson :data="order.custom" />
      </div>

      <LogsTable :entity-id="order.id" class="mt-4"/>

    </div>
    <div v-else class="mx-auto p-6 space-y-8 dark:bg-gray-900 min-h-screen animate-pulse">
      <!-- Nadpis -->
      <div class="h-6 w-48 bg-gray-300 dark:bg-gray-700 rounded"></div>

      <!-- Zákazník + Objednávka -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-2">
          <div class="h-4 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div class="h-3 w-3/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div class="h-3 w-2/3 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-2">
          <div class="h-4 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div class="h-3 w-2/3 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div class="h-3 w-1/3 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      </div>

      <!-- Položky -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-2">
        <div class="h-4 w-1/3 bg-gray-300 dark:bg-gray-700 rounded mb-3"></div>
        <div class="space-y-2">
          <div class="flex space-x-4">
            <div class="h-3 w-1/3 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-1/6 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-1/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
          <div class="flex space-x-4">
            <div class="h-3 w-1/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-1/5 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-1/6 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Custom -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-2">
        <div class="h-4 w-1/4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
        <div class="h-3 w-full bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div class="h-3 w-4/5 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </div>
    </div>

  </default>
</template>
<script lang="ts">
import Default from "@/layouts/admin/default.vue";
import {useAppStore} from "@/stores/appStore";
import {BaseOrderStatus, ResponseEntityOrder} from "@haxo-cz/orders-ts-client";
import RecursiveJson from "@/components/RecursiveJson.vue";
import LogsTable from "@/components/logsTable/logsTable.vue";
import AddressBlock from "@/components/AddressBlock.vue";

export default {
  name: "OrderDetailView",
  components: {AddressBlock, LogsTable, RecursiveJson, Default},
  data() {
    return {
      orderId: this.$route.params.orderId,
      order: null as ResponseEntityOrder | null,
      appStore: useAppStore(),
      productCustomDataVisible: false,
      selectedStatus: null as BaseOrderStatus | null,
      statusLoading: false,
      deleteLoading: false,
      availableStatuses: Object.values(BaseOrderStatus) as BaseOrderStatus[],
    }
  },
  methods: {
    async findOrder() {
      await this.$ordersApi.findOrder(
          this.appStore.selectedProject.spaceId,
          this.appStore.selectedProject._id,
          this.orderId
      ).then(response => {
        this.order = response.data;
        this.selectedStatus = response.data.status;
      }).catch(e => {
        console.log(e)
      });
    },
    async updateStatus() {
      if (!this.selectedStatus || !this.order) return;
      this.statusLoading = true;
      try {
        const response = await this.$ordersApi.updateOrder(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.orderId,
            { status: this.selectedStatus }
        );
        this.order = response.data;
        this.selectedStatus = response.data.status;
      } catch (e) {
        console.error(e);
      } finally {
        this.statusLoading = false;
      }
    },
    async confirmDelete() {
      if (!confirm(`Opravdu smazat objednávku č. ${this.order?.counterNumber}? Tato akce je nevratná.`)) return;
      this.deleteLoading = true;
      try {
        await this.$ordersApi.deleteOrder(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.orderId
        );
        this.$router.push({ name: 'orders' });
      } catch (e) {
        console.error(e);
        this.deleteLoading = false;
      }
    },
    formatDate(date: string | Date): string {
      return new Date(date).toLocaleString('cs-CZ')
    }
  },
  mounted() {
    this.findOrder();
  }
}
</script>